/**
 * Correcting a clocked time, and how often somebody may do it unsupervised.
 *
 * The clock records what it saw. What it saw is often not what happened —
 * somebody started at eight and clocked in at half past, or left at six and
 * remembered the next morning — and the only person who knows the difference is
 * the one who was there. A timesheet that cannot be corrected by that person is
 * a timesheet that stays wrong, because the alternative route (ask a manager,
 * who asks what time it really was, and takes your word for it anyway) costs
 * more than the five minutes it is fixing.
 *
 * So corrections are the employee's to make, up to a number of them a month,
 * and an admin's to approve after that. The allowance is the whole design:
 *
 *   inside it   the correction applies immediately and spends one
 *   beyond it   the correction is recorded as a request and applies on approval
 *   an admin    corrects anything, any number of times, spending nothing
 *
 * Nothing here knows what the number is. It is a stored figure with a
 * per-employee override, and these functions take it as an argument — the same
 * arrangement as every rate in `payroll.ts`, for the same reason.
 */

import type { WorkDayWindow } from "./clock.js";
import { minutesOfDay } from "./clock.js";
import { minutesIntoDay, timeOfDay } from "./timezone.js";

/**
 * The seeded default: five self-service corrections a month.
 *
 * Only ever the value written on first deploy and offered in the settings form.
 * Nothing reads it to make a decision — `effectiveAmendmentLimit` does that from
 * the stored row, which is what an operator can actually change.
 */
export const SELF_TIME_AMENDMENTS_DEFAULT = 5;

/** One employee's own allowance, where they have one, over the business default. */
export function effectiveAmendmentLimit(
  settings: { self_time_amendments_per_month?: number | null } | null | undefined,
  employee: { self_time_amendments_per_month?: number | null } | null | undefined,
): number {
  const own = employee?.self_time_amendments_per_month;
  if (own !== null && own !== undefined && Number.isFinite(Number(own))) {
    return Math.max(0, Math.trunc(Number(own)));
  }

  const standard = settings?.self_time_amendments_per_month;
  if (standard !== null && standard !== undefined && Number.isFinite(Number(standard))) {
    return Math.max(0, Math.trunc(Number(standard)));
  }

  return SELF_TIME_AMENDMENTS_DEFAULT;
}

/** The calendar month an instant falls in, as `YYYY-MM`. */
export function amendmentMonth(isoTimestamp: string): string {
  return isoTimestamp.slice(0, 7);
}

/**
 * One correction, as the counting needs it.
 *
 * `applied_at` rather than `created_at` is what the month is read off, because
 * the month a correction belongs to is the month it took effect in: a request
 * raised on the 31st and approved on the 2nd was supervised, and spends nothing
 * from either month.
 */
export interface CountableAmendment {
  employee_id: string;
  status: string;
  counts_against_allowance: boolean;
  applied_at: string | null;
}

/**
 * How many of this month's allowance an employee has spent.
 *
 * Counted by the month the correction was *made* in, not the month of the day
 * it corrected. "Five a month" is a budget on somebody's own actions, and
 * reading it off the corrected day instead would give a person who fills in a
 * whole quarter at once five per month of back-history — fifteen corrections in
 * an afternoon, which is not what anybody means by five a month.
 */
export function amendmentsUsed(
  rows: readonly CountableAmendment[],
  employeeId: string,
  month: string,
): number {
  return rows.filter(
    (row) =>
      row.employee_id === employeeId &&
      row.status === "applied" &&
      row.counts_against_allowance &&
      !!row.applied_at &&
      amendmentMonth(row.applied_at) === month,
  ).length;
}

export interface AmendmentAllowance {
  /** `YYYY-MM` the figures are for. */
  month: string;
  limit: number;
  used: number;
  remaining: number;
  /** Whether the next correction applies straight away or goes to an admin. */
  needs_approval: boolean;
}

export function amendmentAllowance(limit: number, used: number, month: string): AmendmentAllowance {
  const remaining = Math.max(0, limit - used);
  return { month, limit, used, remaining, needs_approval: remaining === 0 };
}

/**
 * What is wrong with a proposed pair of times, in words, or null.
 *
 * Shared so both ends apply it: the handler refuses a correction it would have
 * to store a contradiction for, and the dialog can say what is wrong before
 * sending a request it knows will be refused.
 *
 * The rules are deliberately few, and each one exists because its absence
 * breaks something downstream rather than because it sounds strict:
 *
 *  - both times stay on the entry's own date, because the date is how the
 *    entry is filed and how the day it contributes to is found. A correction
 *    that moved an entry to another day would silently empty one day's hours
 *    into another's.
 *  - the clock-out stays after the clock-in, because the minutes between them
 *    are the hours, and a negative span is an hour the payroll has to guess at.
 *  - neither time is in the future, because a timesheet is a record.
 *  - the span stays under a day. A single entry longer than that is the
 *    forgotten clock-out this system already closes automatically, and a
 *    correction is not the place to re-open one.
 */
export function amendmentProblem(args: {
  /** The entry's date, which neither corrected time may leave. */
  date: string;
  /** The entry as it stands, so an untouched end can still be checked. */
  startedAt: string;
  endedAt: string | null;
  newStartedAt: string | null;
  newEndedAt: string | null;
  /** Now, so "in the future" can be judged. Defaults to the real clock. */
  now?: Date;
}): string | null {
  const { date, startedAt, endedAt, newStartedAt, newEndedAt } = args;
  const now = args.now ?? new Date();

  if (!newStartedAt && !newEndedAt) return "Change a clock-in or a clock-out time.";

  const start = newStartedAt ?? startedAt;
  const end = newEndedAt ?? endedAt;

  if (newStartedAt && newStartedAt === startedAt && (!newEndedAt || newEndedAt === endedAt)) {
    return "Those are the times already on this entry.";
  }

  for (const [label, value] of [
    ["clock-in", newStartedAt],
    ["clock-out", newEndedAt],
  ] as const) {
    if (!value) continue;
    if (Number.isNaN(Date.parse(value))) return `The ${label} time is not a time.`;
    if (value.slice(0, 10) !== date) {
      return `The ${label} has to stay on ${date} — an entry cannot be moved to another day.`;
    }
    if (Date.parse(value) > now.getTime()) return `The ${label} is in the future.`;
  }

  /*
   * An entry still open has no clock-out to correct, and supplying one here
   * would close it — which is clocking out, retrospectively, and belongs to the
   * clock rather than to a correction.
   */
  if (!endedAt && newEndedAt) {
    return "This entry is still open. Clock out first, then correct the time.";
  }

  if (end && Date.parse(end) <= Date.parse(start)) {
    return "The clock-out has to be after the clock-in.";
  }

  if (end && Date.parse(end) - Date.parse(start) > 86_400_000) {
    return "That is more than a day in one entry.";
  }

  return null;
}

/**
 * The corrected times, as a sentence.
 *
 * For the message a correction is confirmed with and for the request an admin
 * reads. "08:00 instead of 08:34" is the whole of what they need to decide,
 * and a form full of ISO timestamps is not.
 */
export function describeAmendment(args: {
  prevStartedAt: string;
  prevEndedAt: string | null;
  newStartedAt: string | null;
  newEndedAt: string | null;
}): string {
  const clock = (iso: string) => timeOfDay(iso);
  const parts: string[] = [];

  if (args.newStartedAt) {
    parts.push(`in ${clock(args.newStartedAt)} instead of ${clock(args.prevStartedAt)}`);
  }
  if (args.newEndedAt) {
    parts.push(
      `out ${clock(args.newEndedAt)} instead of ${
        args.prevEndedAt ? clock(args.prevEndedAt) : "nothing"
      }`,
    );
  }
  return parts.join(", ");
}

/**
 * Whether a corrected time still falls inside the employee's working day.
 *
 * Not a rule — a correction outside it is allowed, because people do work
 * early and late — but worth saying out loud in the dialog, since a clock-in
 * typed as `18:00` when `08:00` was meant is the mistake this catches and the
 * one that otherwise reads as a full day worked in the evening.
 */
export function outsideWorkingDay(isoTimestamp: string, window: WorkDayWindow): boolean {
  const at = new Date(isoTimestamp);
  if (Number.isNaN(at.getTime())) return false;
  // Against the clock the window is written on. Measured in UTC, every entry a
  // nine-to-five employee ever clocked would read as outside their working day.
  const minutes = minutesIntoDay(at);
  return minutes < minutesOfDay(window.start) || minutes > minutesOfDay(window.end);
}
